import type { DetailedViolation } from "./types";
import { getPatternName, getViolationRange } from "./violationRange";

type FormatDayLabel = (idx?: number) => string;

function createDayFormatter(formatDayLabel?: FormatDayLabel): FormatDayLabel {
  return (idx?: number) => {
    if (formatDayLabel) return formatDayLabel(idx);
    return typeof idx === "number" ? `${idx + 1}일` : "-";
  };
}

export function buildViolationMessage(
  violation: DetailedViolation,
  nurseName: string,
  dayIndex: number,
  groupTotal?: number,
  formatDayLabel?: FormatDayLabel
): string | null {
  const getDay = createDayFormatter(formatDayLabel);

  switch (violation.type) {
    case "manpower_shortage":
      if (violation.day === dayIndex && violation.shift !== undefined) {
        return `${getDay(violation.day)} ${violation.shift} 근무 인원 미달 (필요: ${violation.required}, 배정: ${groupTotal ?? violation.actual})`;
      }
      return null;

    case "manpower_excess":
      if (violation.day === dayIndex && violation.shift !== undefined) {
        return `${getDay(violation.day)} ${violation.shift} 근무 인원 초과 (최대: ${violation.required_max}, 배정: ${groupTotal ?? violation.actual})`;
      }
      return null;

    case "max_conseq_work":
      if (
        violation.start_day !== undefined &&
        violation.end_day !== undefined &&
        dayIndex >= violation.start_day &&
        dayIndex <= violation.end_day
      ) {
        return `${nurseName} 연속 근무일 초과 (최대: ${violation.max}일, 현재: ${violation.actual}일)`;
      }
      return null;

    case "three_seq_nig":
      if (
        violation.start_day !== undefined &&
        violation.end_day !== undefined &&
        dayIndex >= violation.start_day &&
        dayIndex <= violation.end_day
      ) {
        return `${nurseName} N 3연속 근무 위반 (${getDay(violation.start_day)}~${getDay(violation.end_day)})`;
      }
      return null;

    case "banned_day_after_eve":
      if (violation.day === dayIndex || violation.next_day === dayIndex) {
        const patternName = getPatternName(violation.pattern);
        return `${nurseName} ${patternName} 근무 위반 (${getDay(violation.day)} → ${getDay(violation.next_day)})`;
      }
      return null;

    case "max_nig_per_month":
      return `${nurseName} 월 나이트 근무 초과 (최대: ${violation.max}회, 현재: ${violation.actual}회)`;

    case "two_offs_after_two_nig": {
      const range = getViolationRange(violation);
      if (range && dayIndex >= range.startDay && dayIndex <= range.endDay) {
        const offStart =
          violation.off_start_day ??
          (typeof violation.start_day === "number"
            ? violation.start_day + 2
            : undefined);
        const offEnd =
          violation.off_end_day ??
          (typeof violation.start_day === "number"
            ? violation.start_day + 3
            : undefined);
        const offRange =
          offStart === offEnd
            ? getDay(offStart)
            : `${getDay(offStart)}~${getDay(offEnd)}`;
        return `${nurseName} N 2회 후 OFF 미준수 (${getDay(violation.start_day)}~${getDay(typeof violation.start_day === "number" ? violation.start_day + 1 : undefined)} N 후 ${offRange} OFF 필요)`;
      }
      return null;
    }

    case "two_offs_after_three_nig": {
      const range = getViolationRange(violation);
      if (range && dayIndex >= range.startDay && dayIndex <= range.endDay) {
        const offStart =
          violation.off_start_day ??
          (typeof violation.start_day === "number"
            ? violation.start_day + 3
            : undefined);
        const offEnd =
          violation.off_end_day ??
          (typeof violation.start_day === "number"
            ? violation.start_day + 4
            : undefined);
        const offRange =
          offStart === offEnd
            ? getDay(offStart)
            : `${getDay(offStart)}~${getDay(offEnd)}`;
        return `${nurseName} N 3회 후 OFF 미준수 (${getDay(violation.start_day)}~${getDay(typeof violation.start_day === "number" ? violation.start_day + 2 : undefined)} N 후 ${offRange} OFF 필요)`;
      }
      return null;
    }

    default:
      return null;
  }
}
